import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { todayLocal } from "../utils/dateLocal";
import axios from "axios";
import {
  FilePlus, Search, Save, Edit, Trash2, Printer, FileText,
  DoorClosed, Wrench,
} from "lucide-react";
import { openReport, buildReportName } from "../lib/reportViewerBus";
import DateInput from "./DateInput";
import { WinButton } from "./WinComponents";
import VilleAutocomplete from "./VilleAutocomplete";
import { validateCourNo, formatCourNo, cleanCourNo, courNoMessage, maskCourNo } from "../lib/courNoValidation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal Comparution — parité stricte VB.NET `FrmComp` + `CodeComparution.vb`.
 * -----------------------------------------------------------------------------
 * Structure legacy (3 onglets) :
 *   Tab 1 (Requérant)   : Nom/Prénom/Naissance/Sexe/Adresse/Téléphones/Courriel + Détenu?
 *   Tab 2 (Comparution) : Date+Heure comparution, Avocat, Code/Article/Accessoire,
 *                          Étape, Numéro de dossier de cour, Objet
 *   Tab 3 (Rencontre)   : Liste des rencontres (grid) + saisie
 *
 * Convention DAJ : NoSeqDaj se termine par -00 (suffixe distinctif comparution).
 */

// Helpers de masques (parité `MaskedTextBox` VB.NET)
const _formatPostalCA = (v) => {
  // Format canadien A0A 0A0 — alternance stricte lettre/chiffre.
  // Positions : 0=L, 1=D, 2=L, 3=D, 4=L, 5=D.
  const clean = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const out = [];
  for (let i = 0; i < clean.length && i < 6; i++) {
    const c = clean[i];
    const shouldBeLetter = (i === 0 || i === 2 || i === 4);
    if (shouldBeLetter && !/[A-Z]/.test(c)) continue;   // rejette chiffre en pos lettre
    if (!shouldBeLetter && !/[0-9]/.test(c)) continue;  // rejette lettre en pos chiffre
    out.push(c);
  }
  const s = out.join("");
  return s.length > 3 ? `${s.slice(0, 3)} ${s.slice(3)}` : s;
};
const _isValidPostalCA = (v) => /^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]$/.test((v || "").toUpperCase());
const _isValidEmail = (v) => {
  if (!v) return true;   // vide = pas d'erreur (champ optionnel)
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
};
const _formatPhone = (v) => {
  const s = (v || "").replace(/\D/g, "").slice(0, 10);
  if (s.length < 4) return s;
  if (s.length < 7) return `(${s.slice(0, 3)}) ${s.slice(3)}`;
  return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
};

const EMPTY_COMP = {
  noreg: "90", nobur: "01", noseqdaj: "",
  date_appel: todayLocal(),
  nom: "", prenom: "", date_naissance: "",
  sexe: 1, nas: "", sed: "",
  adresse: "", ville: "", province: "QC", code_postal: "",
  tel_res: "", tel_bur: "", courriel: "",
  detenu: 0,
  date_comparution: todayLocal(),
  heure_comparution: "",
  avocat_code: "", avocat_nom: "",
  code_nature: "", article: "", nature_accessoire: "",
  etape: "", no_dossier_cour: "", objet: "",
  noclient: "", pk_daj: "",
};

const EMPTY_RENC = {
  rowid: "", date: todayLocal(),
  heure: "", lieu: "", local: "",
  rencontre: 1, tel1: "", tel2: "", remarque: "",
};

// Styles WinForms 3D locaux
const cellStyle = { border: "1px solid #808080", padding: "3px 6px",
                    background: "#fff", fontSize: 11 };
const labelStyle = { background: "#e8e8e8", padding: "3px 6px", fontSize: 11,
                     fontWeight: 600, border: "1px solid #808080",
                     borderRight: "none", width: 140 };
const inp = { border: "none", outline: "none", width: "100%", fontSize: 11,
              background: "transparent" };

function Cell({ label, children, testid, colSpan }) {
  return (
    <tr>
      <td style={labelStyle}>{label}</td>
      <td style={cellStyle} colSpan={colSpan}
          data-testid={testid ? `${testid}-cell` : undefined}>
        {children}
      </td>
    </tr>
  );
}


export default function ComparutionModal({ isOpen, onClose, showPopup, onOpenDemande }) {
  const [comp, setComp] = useState(EMPTY_COMP);
  const [activeTab, setActiveTab] = useState("requerant");
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(true);  // true=modifiable, false=lecture
  const [etapes, setEtapes] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [natures, setNatures] = useState([]);
  const [avocats, setAvocats] = useState([]);   // codes commençant par A ou P
  // Rencontres
  const [rencontres, setRencontres] = useState([]);
  const [rencForm, setRencForm] = useState(EMPTY_RENC);
  // Infocour (multiples dossiers)
  const [infocours, setInfocours] = useState([]);
  const [showInfocour, setShowInfocour] = useState(false);
  // Recherche
  const [showRecherche, setShowRecherche] = useState(false);
  const [searchCrit, setSearchCrit] = useState({
    nom: "", prenom: "", date_naissance: "", noseqdaj: "",
    no_dossier_cour: "", noreg: "90", nobur: "01",
  });
  const [searchResults, setSearchResults] = useState([]);
  // Validation « No dossier de cour » (adda544) — comparution (mode strict)
  const [compCourSansMasque, setCompCourSansMasque] = useState(false);
  const courInputRef = useRef(null);
  const icCourInputRef = useRef(null);

  // ------------- Chargement combos référence -------------
  // Extrait dans un helper réutilisable pour permettre le rechargement
  // manuel sur clic « Nouveau » (au cas où le 1er fetch aurait échoué —
  // ex. backend pas encore prêt à l'ouverture initiale de la modale).
  const _normalizeCombo = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map(x => {
      if (typeof x === "string") return { code: x, description: x };
      return { code: x.code || x.description, description: x.description || x.code };
    }).filter(x => x.description);
  };

  const loadCombos = useCallback(() => {
    axios.get(`${API}/reference/etapes`)
      .then(r => setEtapes(_normalizeCombo(r.data)))
      .catch(err => {
        console.warn("[Comparution] etapes indisponibles:", err?.message);
        setEtapes([]);
      });
    axios.get(`${API}/reference/lieux-detention`)
      .then(r => setLieux(_normalizeCombo(r.data)))
      .catch(err => {
        console.warn("[Comparution] lieux-detention indisponibles:", err?.message);
        setLieux([]);
      });
    axios.get(`${API}/reference/natures-comparution`)
      .then(r => setNatures(r.data || []))
      .catch(err => {
        console.warn("[Comparution] natures-comparution indisponibles:", err?.message);
        setNatures([]);
      });
    axios.get(`${API}/reference/avocats`, { params: { prefix: "A,P" } })
      .then(r => setAvocats(r.data || []))
      .catch(err => {
        console.warn("[Comparution] avocats indisponibles:", err?.message);
        setAvocats([]);
      });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadCombos();
  }, [isOpen, loadCombos]);

  // ------------- Reset à l'ouverture -------------
  useEffect(() => {
    if (isOpen) {
      setComp(EMPTY_COMP);
      setActiveTab("requerant");
      setEditMode(true);
      setRencontres([]);
      setInfocours([]);
      setRencForm(EMPTY_RENC);
    }
  }, [isOpen]);

  // ------------- Recharger rencontres/infocour quand pk_daj change -------------
  const reloadNested = useCallback(async (pk) => {
    if (!pk) { setRencontres([]); setInfocours([]); return; }
    try {
      const [rr, ic] = await Promise.all([
        axios.get(`${API}/comparution/${encodeURIComponent(pk)}/rencontres`),
        axios.get(`${API}/comparution/${encodeURIComponent(pk)}/infocour`),
      ]);
      setRencontres(rr.data || []);
      setInfocours(ic.data || []);
    } catch (_) { /* silencieux */ }
  }, []);

  // ------------- Actions toolbar -------------
  const doNouveau = () => {
    setComp(EMPTY_COMP);
    setActiveTab("requerant");
    setEditMode(true);
    setRencontres([]);
    setInfocours([]);
    // Recharger les combos — au cas où un fetch initial aurait échoué
    // ou où les combos auraient été vidés entre-temps.
    loadCombos();
  };

  const doSave = async () => {
    if (!comp.date_appel || !comp.nom?.trim() || !comp.prenom?.trim()
        || !comp.date_naissance) {
      showPopup?.("Le nom, le prénom et la date de naissance sont obligatoires.",
                  "warning", "Enregistrer");
      return;
    }
    if (comp.courriel && !_isValidEmail(comp.courriel)) {
      showPopup?.(`L'adresse courriel « ${comp.courriel} » est invalide.\n`
                  + `Format attendu : nom@domaine.ca`,
                  "warning", "Enregistrer");
      return;
    }
    if (comp.code_postal && !_isValidPostalCA(comp.code_postal)) {
      showPopup?.(`Le code postal « ${comp.code_postal} » est invalide.\n`
                  + `Format attendu : A0A 0A0 (ex. H0H 0H0).`,
                  "warning", "Enregistrer");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/comparution`, comp);
      setComp(c => ({ ...c, pk_daj: r.data.pk_daj,
                     noseqdaj: r.data.noseqdaj,
                     noclient: r.data.noclient }));
      await reloadNested(r.data.pk_daj);
      setEditMode(false);
      showPopup?.(`Comparution enregistrée (${r.data.noseqdaj}).`,
                  "info", "Enregistrer");
    } catch (e) {
      showPopup?.(e?.response?.data?.detail || String(e), "error", "Enregistrer");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!comp.pk_daj) {
      showPopup?.("Aucune comparution à supprimer.", "warning", "Supprimer");
      return;
    }
    if (!window.confirm("Supprimer cette comparution et toutes ses rencontres/dossiers de cour ?"))
      return;
    try {
      await axios.delete(`${API}/comparution/${encodeURIComponent(comp.pk_daj)}`);
      doNouveau();
      showPopup?.("Comparution supprimée.", "info", "Supprimer");
    } catch (e) {
      showPopup?.(e?.response?.data?.detail || String(e), "error", "Supprimer");
    }
  };

  const doCreerDaj = async () => {
    if (!comp.pk_daj) {
      showPopup?.("Enregistrez d'abord la comparution.", "warning", "Créer Daj");
      return;
    }
    // Parité legacy FrmComp.vb:167 : confirmation AVANT toute création
    if (!window.confirm("Générer une demande d'aide juridique ?")) return;
    // Parité legacy : la cause principale (adda544) reste sur la demande de
    // base — seules les causes SUPPLÉMENTAIRES génèrent des demandes en plus,
    // et la confirmation ne s'affiche que s'il y en a.
    const mainCause = (comp.no_dossier_cour || "").trim();
    const extra = infocours.filter(
      ic => !mainCause || (ic.no_cause || "").trim() !== mainCause
    ).length;
    if (extra > 0) {
      const n = extra + 1;
      if (!window.confirm(`${n} demandes d'aide juridique seront générées à partir de cet appel de comparution.`))
        return;
    }
    try {
      const r = await axios.post(`${API}/comparution/${encodeURIComponent(comp.pk_daj)}/creer-daj`);
      showPopup?.(`Séquences générées :\n${r.data.generated.join(", ")}`,
                  "info", "Comparution");
      // Parité legacy : « Visualiser la demande d'aide juridique créée ? » (vbYesNo)
      if (window.confirm("Visualiser la demande d'aide juridique créée ?")) {
        onOpenDemande?.({
          no_client: r.data.noclient,
          no_seq_daj: r.data.generated[0],
          pk_daj: r.data.first_pk_daj,
          no_bur: r.data.no_bur,
        });
        onClose?.();
        return;
      }
      doNouveau();
    } catch (e) {
      showPopup?.(e?.response?.data?.detail || String(e), "error", "Créer Daj");
    }
  };

  const doPrint = () => {
    if (!comp.pk_daj) {
      showPopup?.("Aucune comparution chargée. Enregistrez d'abord la demande.",
                  "warning", "Imprimer");
      return;
    }
    // Ouvre le PDF « Autorisation à comparaître » dans la visionneuse (avec
    // bouton « Enregistrer en PDF »). Parité avec les autres rapports.
    openReport({
      url: `${API}/comparution/${encodeURIComponent(comp.pk_daj)}/pdf`,
      title: "Autorisation à comparaître",
      fileName: buildReportName("COMPARUTION", comp.noseqdaj),
    });
  };

  // ------------- Recherche -------------
  const doSearch = async () => {
    try {
      const params = Object.fromEntries(
        Object.entries(searchCrit).filter(([, v]) => v && String(v).trim()));
      const r = await axios.get(`${API}/comparution/search/list`, { params });
      setSearchResults(r.data || []);
    } catch (e) {
      showPopup?.(String(e), "error", "Rechercher");
    }
  };

  const doOpen = async (pk, rowData) => {
    // Fallback : reconstruit pk_daj si absent (parité SQL Server où
    // la colonne calculée pk_daj n'existe pas toujours dans le SELECT).
    let effectivePk = (pk || "").trim();
    if (!effectivePk && rowData) {
      const rr = String(rowData.noreg || "").padStart(2, "0").slice(0, 2);
      const bb = String(rowData.nobur || "").padStart(2, "0").slice(0, 2);
      effectivePk = `${rr}${bb}${rowData.noseqdaj || ""}`;
    }
    if (!effectivePk) {
      showPopup?.("Impossible de retrouver la comparution : identifiant manquant.",
                  "error", "Ouvrir");
      return;
    }
    try {
      const r = await axios.get(`${API}/comparution/${encodeURIComponent(effectivePk)}`);
      const d = r.data;
      setComp({
        noreg: d.noreg || "90", nobur: d.nobur || "01",
        noseqdaj: d.noseqdaj || "", pk_daj: d.pk_daj || effectivePk,
        noclient: d.noclient || "",
        date_appel: (d.date_appel || "").slice(0, 10),
        nom: d.nom || "", prenom: d.prenom || "",
        date_naissance: (d.date_naissance || "").slice(0, 10),
        sexe: d.sexe ?? 1, nas: d.nas || "", sed: d.sed || "",
        adresse: d.adresse || "", ville: d.ville || "",
        province: d.province || "QC", code_postal: d.code_postal || "",
        tel_res: d.tel_res || "", tel_bur: d.tel_bur || "",
        courriel: d.courriel || "", detenu: d.detenu ?? 0,
        date_comparution: (d.date_comparution || "").slice(0, 10),
        heure_comparution: d.heure_comparution || "",
        avocat_code: d.avocat_code || "", avocat_nom: d.avocat_nom || "",
        code_nature: d.code_nature || "", article: d.article || "",
        nature_accessoire: d.nature_accessoire || "",
        etape: d.etape || "", no_dossier_cour: d.no_dossier_cour || "",
        objet: d.objet || "",
      });
      await reloadNested(effectivePk);
      setEditMode(false);
      setShowRecherche(false);
      // Basculer sur l'onglet Requérant pour voir les infos chargées
      setActiveTab("requerant");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      showPopup?.(`Erreur au chargement : ${detail}`, "error", "Ouvrir");
      console.error("[Comparution] doOpen failed:", e);
    }
  };

  // ------------- Rencontres CRUD -------------
  const saveRenc = async () => {
    if (!comp.pk_daj) {
      showPopup?.("Enregistrez d'abord la comparution.", "warning", "Rencontre");
      return;
    }
    try {
      if (rencForm.rowid) {
        await axios.put(`${API}/comparution/${encodeURIComponent(comp.pk_daj)}/rencontres/${rencForm.rowid}`,
                        rencForm);
      } else {
        await axios.post(`${API}/comparution/${encodeURIComponent(comp.pk_daj)}/rencontres`,
                         rencForm);
      }
      await reloadNested(comp.pk_daj);
      setRencForm(EMPTY_RENC);
    } catch (e) {
      showPopup?.(String(e), "error", "Rencontre");
    }
  };

  const delRenc = async (rowid) => {
    if (!window.confirm("Supprimer cette rencontre ?")) return;
    try {
      await axios.delete(`${API}/comparution/${encodeURIComponent(comp.pk_daj)}/rencontres/${rowid}`);
      await reloadNested(comp.pk_daj);
    } catch (e) {
      showPopup?.(String(e), "error", "Rencontre");
    }
  };

  // ------------- Numéros de cause / Infocour (parité `FrmNoCauses`) -------
  // État complet du sous-formulaire : liste, sélection, formulaire d'édition,
  // et mode (view/new/edit).
  const EMPTY_IC = {
    rowid: "", no_cause: "", code_nature: "", code_criminel: "",
    etape: "", note: "", article: "", sans_mask: false,
  };
  const [icForm, setIcForm] = useState(EMPTY_IC);
  const [icMode, setIcMode] = useState("view");   // view | new | edit
  const [icSelectedRowId, setIcSelectedRowId] = useState("");

  const selectIcRow = (row) => {
    setIcForm({
      rowid: row.rowid, no_cause: row.no_cause || "",
      code_nature: row.code_nature || "", code_criminel: row.code_criminel || "",
      etape: row.etape || "", note: row.note || "",
      article: row.code_criminel || "", sans_mask: false,
    });
    setIcSelectedRowId(row.rowid);
    setIcMode("view");
  };

  const icNouveau = () => {
    setIcForm(EMPTY_IC);
    setIcSelectedRowId("");
    setIcMode("new");
  };

  const icModifier = () => {
    if (!icSelectedRowId) return;
    setIcMode("edit");
  };

  // Validation du « No. de cause » (infocour) — mode STRICT (parité FrmNoCauses) :
  // alerte + vide + refocus. Ignorée si « Sans Mask » coché.
  const handleIcCourBlur = (val) => {
    if (icForm.sans_mask || icMode === "view") return;
    const res = validateCourNo(val);
    if (!res.ok) {
      showPopup?.(courNoMessage(res.reason), "warning", "Numéros de cause");
      setIcForm(f => ({ ...f, no_cause: "" }));
      setTimeout(() => icCourInputRef.current?.focus(), 0);
    } else {
      const c = cleanCourNo(val);
      if (c.length === 14) setIcForm(f => ({ ...f, no_cause: formatCourNo(val) }));
    }
  };

  const icEnregistrer = async () => {
    if (!comp.pk_daj) return;
    if (!icForm.no_cause?.trim()) {
      showPopup?.("Le numéro de cause est obligatoire.", "warning", "Numéros de cause");
      return;
    }
    // Validation du numéro de cause (parité FrmNoCauses) — ignorée si « Sans Mask »
    if (!icForm.sans_mask) {
      const vres = validateCourNo(icForm.no_cause);
      if (!vres.ok) {
        showPopup?.(courNoMessage(vres.reason), "warning", "Numéros de cause");
        return;
      }
    }
    // Anti-doublon (parité FrmNoCauses.ValiderCle) — uniquement pour un NOUVEAU
    // numéro (pas en édition) : rejette si le numéro existe déjà dans la liste.
    if (icMode !== "edit") {
      const target = cleanCourNo(icForm.no_cause);
      if (infocours.some(ic => cleanCourNo(ic.no_cause) === target)) {
        showPopup?.("Il faut avoir un numéro de cour différent.", "warning", "Numéros de cause");
        setTimeout(() => icCourInputRef.current?.focus(), 0);
        return;
      }
    }
    const body = {
      no_cause: icForm.no_cause,
      code_nature: icForm.code_nature,
      code_criminel: icForm.article,  // article = code criminel
      etape: icForm.etape,
      note: icForm.note,
    };
    try {
      if (icMode === "edit" && icForm.rowid) {
        await axios.put(
          `${API}/comparution/${encodeURIComponent(comp.pk_daj)}/infocour/${icForm.rowid}`,
          body);
      } else {
        await axios.post(
          `${API}/comparution/${encodeURIComponent(comp.pk_daj)}/infocour`, body);
      }
      await reloadNested(comp.pk_daj);
      // Sync inverse : si la cause modifiée = cause principale (adda544),
      // le backend a mis à jour daj.compbutnot. On recharge le formulaire
      // Comparution pour refléter le nouveau `objet`.
      if (icForm.no_cause === comp.no_dossier_cour) {
        const r = await axios.get(
          `${API}/comparution/${encodeURIComponent(comp.pk_daj)}`);
        const d = r.data;
        setComp(c => ({
          ...c,
          objet: d.objet || "",
          code_nature: d.code_nature || c.code_nature,
          article: d.article || c.article,
          etape: d.etape || c.etape,
          no_dossier_cour: d.no_dossier_cour || c.no_dossier_cour,
        }));
      }
      setIcMode("view");
    } catch (e) {
      showPopup?.(e?.response?.data?.detail || String(e),
                  "error", "Numéros de cause");
    }
  };

  const icSupprimer = async () => {
    if (!icSelectedRowId) return;
    if (!window.confirm("Supprimer ce numéro de cause ?")) return;
    try {
      await axios.delete(
        `${API}/comparution/${encodeURIComponent(comp.pk_daj)}/infocour/${icSelectedRowId}`);
      await reloadNested(comp.pk_daj);
      setIcForm(EMPTY_IC);
      setIcSelectedRowId("");
      setIcMode("view");
    } catch (e) {
      showPopup?.(String(e), "error", "Numéros de cause");
    }
  };

  const icCloseModal = () => {
    setShowInfocour(false);
    setIcForm(EMPTY_IC);
    setIcSelectedRowId("");
    setIcMode("view");
  };

  if (!isOpen) return null;

  const disabled = !editMode;
  const change = (k, v) => setComp(c => ({ ...c, [k]: v }));

  // Validation « No dossier de cour » (adda544) — mode STRICT (comparution) :
  // alerte + vider + refocus. Case « sans masque » = échappatoire Legacy.
  const handleCompCourBlur = (val) => {
    if (compCourSansMasque) return;
    const res = validateCourNo(val);
    if (!res.ok) {
      showPopup?.(courNoMessage(res.reason), "warning", "No dossier de cour");
      change("no_dossier_cour", "");
      setTimeout(() => courInputRef.current?.focus(), 0);
    } else {
      const c = cleanCourNo(val);
      if (c.length === 14) change("no_dossier_cour", formatCourNo(val));
    }
  };

  return (
    <div className="popup-overlay" data-testid="modal-comparution" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#dbe6f0", border: "1px solid #808080",
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        width: "min(1180px, 98vw)", maxHeight: "95vh", overflow: "auto",
        display: "flex", flexDirection: "column",
      }}>
        {/* Titre */}
        <div style={{
          background: "linear-gradient(to bottom, #dbe6f0, #b8ccd8)",
          padding: "4px 8px", fontSize: 12, fontWeight: 600, color: "#111",
          borderBottom: "1px solid #808080", display: "flex",
          justifyContent: "space-between",
        }}>
          <span data-testid="comp-titre">Appel de comparution
            {comp.pk_daj ? `  —  ${comp.noseqdaj}` : ""}</span>
          <button onClick={onClose} data-testid="btn-comp-close-x"
                  style={{ background: "transparent", border: "none",
                           cursor: "pointer", fontSize: 14, fontWeight: 700 }}>×</button>
        </div>

        {/* Toolbar */}
        <div className="win-toolbar" style={{ padding: "8px 12px", gap: 8, display: "flex", flexWrap: "wrap" }}>
          <WinButton IconComponent={FilePlus} onClick={doNouveau}
                     data-testid="btn-comp-nouveau">Nouvelle</WinButton>
          <WinButton IconComponent={Search}
                     onClick={() => { setShowRecherche(true); setSearchResults([]); }}
                     data-testid="btn-comp-rechercher">Rechercher</WinButton>
          <WinButton IconComponent={Save} onClick={doSave}
                     disabled={saving || !editMode}
                     data-testid="btn-comp-enregistrer">
            {saving ? "..." : "Enregistrer"}
          </WinButton>
          <WinButton IconComponent={Edit} onClick={() => setEditMode(true)}
                     disabled={editMode || !comp.pk_daj}
                     data-testid="btn-comp-modifier">Modifier</WinButton>
          <WinButton IconComponent={Trash2} onClick={doDelete}
                     disabled={!comp.pk_daj}
                     data-testid="btn-comp-supprimer">Supprimer</WinButton>
          <WinButton IconComponent={Printer} onClick={doPrint}
                     data-testid="btn-comp-imprimer">Imprimer</WinButton>
          <WinButton IconComponent={FileText} onClick={doCreerDaj}
                     disabled={!comp.pk_daj}
                     data-testid="btn-comp-creer-daj">Créer Daj</WinButton>
          <WinButton IconComponent={Wrench} onClick={() => setShowInfocour(true)}
                     disabled={!comp.pk_daj}
                     data-testid="btn-comp-outils-multi">Multiples dossiers</WinButton>
          <WinButton IconComponent={DoorClosed} onClick={onClose}
                     data-testid="btn-comp-fermer">Fermer</WinButton>
        </div>

        {/* En-tête dossier */}
        <div style={{ background: "#c8d4dc", padding: "6px 12px",
                       borderTop: "1px solid #808080", borderBottom: "1px solid #808080" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
            <tbody>
              <tr>
                <td style={{ ...labelStyle, width: 80 }}>Région</td>
                <td style={cellStyle}>
                  <input value={comp.noreg} onChange={e => change("noreg", e.target.value)}
                         disabled={disabled} style={{ ...inp, width: 40 }}
                         data-testid="comp-noreg" />
                </td>
                <td style={{ ...labelStyle, width: 80 }}>Bureau</td>
                <td style={cellStyle}>
                  <input value={comp.nobur} onChange={e => change("nobur", e.target.value)}
                         disabled={disabled} style={{ ...inp, width: 40 }}
                         data-testid="comp-nobur" />
                </td>
                <td style={{ ...labelStyle, width: 120 }}>No. Séquence</td>
                <td style={cellStyle}>
                  <input value={comp.noseqdaj} readOnly style={{ ...inp, width: 140 }}
                         data-testid="comp-noseq" />
                </td>
                <td style={{ ...labelStyle, width: 80 }}>Date</td>
                <td style={cellStyle}>
                  <DateInput value={comp.date_appel}
                             onChange={v => change("date_appel", v)}
                             disabled={disabled}
                             data-testid="comp-date-appel" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Onglets */}
        <div style={{ display: "flex", gap: 2, padding: "6px 12px 0",
                       background: "#dbe6f0" }}>
          {[
            { id: "requerant", label: "Requérant" },
            { id: "comparution", label: "Comparution" },
            { id: "rencontre", label: "Rencontre" },
          ].map(t => (
            <div key={t.id} onClick={() => setActiveTab(t.id)}
                 data-testid={`comp-tab-${t.id}`}
                 className={activeTab === t.id ? "win-tab-active" : "win-tab"}
                 style={{
                   padding: "4px 14px", fontSize: 11, cursor: "pointer",
                   background: activeTab === t.id ? "#fff" : "#c8d4dc",
                   border: "1px solid #808080",
                   borderBottom: activeTab === t.id ? "1px solid #fff" : "1px solid #808080",
                   fontWeight: activeTab === t.id ? 700 : 400,
                 }}>{t.label}</div>
          ))}
        </div>

        {/* Corps */}
        <div className="win-tab-content" style={{ background: "#fff",
              border: "1px solid #808080", margin: "0 12px 12px",
              padding: 8, minHeight: 400 }}>

          {activeTab === "requerant" && (
            <div>
              <div style={{ background: "#0d7377", color: "#fff",
                             padding: "4px 8px", fontSize: 11, fontWeight: 700,
                             textAlign: "center", marginBottom: 6 }}>Requérant</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                  <Cell label="Nom" testid="comp-nom">
                    <input value={comp.nom} onChange={e => change("nom", e.target.value)}
                           disabled={disabled} style={inp} data-testid="comp-nom" />
                  </Cell>
                  <Cell label="Prénom" testid="comp-prenom">
                    <input value={comp.prenom} onChange={e => change("prenom", e.target.value)}
                           disabled={disabled} style={inp} data-testid="comp-prenom" />
                  </Cell>
                  <Cell label="Date de naissance" testid="comp-dnaiss">
                    <DateInput value={comp.date_naissance}
                               onChange={v => change("date_naissance", v)}
                               disabled={disabled} data-testid="comp-date-naissance" />
                  </Cell>
                  <Cell label="Sexe">
                    <label style={{ marginRight: 12 }}>
                      <input type="radio" checked={comp.sexe === 0}
                             onChange={() => change("sexe", 0)}
                             disabled={disabled} data-testid="comp-sexe-f" /> Fém.
                    </label>
                    <label>
                      <input type="radio" checked={comp.sexe === 1}
                             onChange={() => change("sexe", 1)}
                             disabled={disabled} data-testid="comp-sexe-m" /> Masc.
                    </label>
                  </Cell>
                  <Cell label="N.A.S.">
                    <input value={comp.nas} onChange={e => change("nas", e.target.value)}
                           disabled={disabled} style={inp} data-testid="comp-nas" />
                  </Cell>
                  <Cell label="S.E.D.">
                    <input value={comp.sed} onChange={e => change("sed", e.target.value)}
                           disabled={disabled} style={inp} data-testid="comp-sed" />
                  </Cell>
                  <Cell label="Détenu ?">
                    <label style={{ marginRight: 12 }}>
                      <input type="radio" checked={comp.detenu === 1}
                             onChange={() => change("detenu", 1)}
                             disabled={disabled} data-testid="comp-detenu-oui" /> Oui
                    </label>
                    <label>
                      <input type="radio" checked={comp.detenu === 0}
                             onChange={() => change("detenu", 0)}
                             disabled={disabled} data-testid="comp-detenu-non" /> Non
                    </label>
                  </Cell>
                </tbody></table>

                <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                  <Cell label="Adresse">
                    <input value={comp.adresse} onChange={e => change("adresse", e.target.value)}
                           disabled={disabled} style={inp} data-testid="comp-adresse" />
                  </Cell>
                  <Cell label="Ville">
                    <VilleAutocomplete value={comp.ville} onChange={v => change("ville", v)}
                           disabled={disabled} className="" style={inp} testId="comp-ville" />
                  </Cell>
                  <Cell label="Code postal">
                    <input value={comp.code_postal}
                           onChange={e => change("code_postal", _formatPostalCA(e.target.value))}
                           disabled={disabled} placeholder="A0A 0A0"
                           maxLength={7}
                           style={{ ...inp, width: 100, fontFamily: "monospace",
                                    textTransform: "uppercase",
                                    ...(comp.code_postal
                                        && !_isValidPostalCA(comp.code_postal)
                                        ? { border: "1px solid #c00",
                                            outline: "1px solid #c00" }
                                        : {}) }}
                           data-testid="comp-cp" />
                  </Cell>
                  <Cell label="Province">
                    <input value={comp.province} onChange={e => change("province", e.target.value)}
                           disabled={disabled} style={{ ...inp, width: 60 }}
                           data-testid="comp-province" />
                  </Cell>
                  <Cell label="Téléphone rés.">
                    <input value={comp.tel_res}
                           onChange={e => change("tel_res", _formatPhone(e.target.value))}
                           disabled={disabled} placeholder="(999) 999-9999"
                           maxLength={14}
                           style={{ ...inp, fontFamily: "monospace" }}
                           data-testid="comp-tel-res" />
                  </Cell>
                  <Cell label="Téléphone bur.">
                    <input value={comp.tel_bur}
                           onChange={e => change("tel_bur", _formatPhone(e.target.value))}
                           disabled={disabled} placeholder="(999) 999-9999"
                           maxLength={14}
                           style={{ ...inp, fontFamily: "monospace" }}
                           data-testid="comp-tel-bur" />
                  </Cell>
                  <Cell label="Courriel">
                    <input value={comp.courriel} onChange={e => change("courriel", e.target.value)}
                           disabled={disabled} type="email"
                           placeholder="nom@domaine.ca"
                           style={{ ...inp,
                                    ...(comp.courriel && !_isValidEmail(comp.courriel)
                                        ? { border: "1px solid #c00",
                                            outline: "1px solid #c00" }
                                        : {}) }}
                           data-testid="comp-courriel" />
                    {comp.courriel && !_isValidEmail(comp.courriel) && (
                      <div data-testid="courriel-error"
                           style={{ color: "#c00", fontSize: 10, marginTop: 2 }}>
                        Adresse courriel invalide
                      </div>
                    )}
                  </Cell>
                </tbody></table>
              </div>
            </div>
          )}

          {activeTab === "comparution" && (
            <div>
              <div style={{ background: "#0d7377", color: "#fff",
                             padding: "4px 8px", fontSize: 11, fontWeight: 700,
                             textAlign: "center", marginBottom: 6 }}>Comparution</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                  <Cell label="Date comparution">
                    <DateInput value={comp.date_comparution}
                               onChange={v => change("date_comparution", v)}
                               disabled={disabled}
                               data-testid="comp-date-comp" />
                  </Cell>
                  <Cell label="Heure de comparution">
                    <input value={comp.heure_comparution}
                           onChange={e => change("heure_comparution", e.target.value)}
                           disabled={disabled} placeholder="HH:MM" style={inp}
                           data-testid="comp-heure" />
                  </Cell>
                  <Cell label="Avocat">
                    <select value={comp.avocat_code}
                            onChange={e => {
                              const code = e.target.value;
                              const av = avocats.find(a => a.code === code);
                              setComp(c => ({
                                ...c, avocat_code: code,
                                avocat_nom: av ? av.label : "",
                              }));
                            }}
                            disabled={disabled}
                            style={{ ...inp, background: "#fff" }}
                            data-testid="comp-avocat-code">
                      <option value="">-- Sélectionner un avocat --</option>
                      {avocats.map(a => (
                        <option key={a.code} value={a.code}>
                          {a.code} — {a.label}
                        </option>
                      ))}
                    </select>
                  </Cell>
                  <Cell label="Objet">
                    <textarea value={comp.objet}
                              onChange={e => change("objet", e.target.value)}
                              disabled={disabled} rows={3}
                              style={{ ...inp, resize: "vertical" }}
                              data-testid="comp-objet" />
                  </Cell>
                </tbody></table>

                <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                  <Cell label="Article">
                    <input value={comp.article}
                           onChange={e => change("article", e.target.value.slice(0, 5))}
                           maxLength={5}
                           disabled={disabled} style={inp} data-testid="comp-article" />
                  </Cell>
                  <Cell label="Code Nature">
                    <select value={comp.code_nature}
                            onChange={e => {
                              const v = e.target.value;
                              setComp(c => ({
                                ...c,
                                code_nature: v,
                                // Parité legacy : la sélection d'un code nature
                                // pré-remplit l'Article avec ses 5 premiers
                                // caractères (l'utilisateur peut ensuite le
                                // modifier manuellement).
                                article: v ? v.slice(0, 5) : c.article,
                              }));
                            }}
                            disabled={disabled} style={{ ...inp, background: "#fff" }}
                            data-testid="comp-code-nature">
                      <option value="">-- Sélectionner --</option>
                      {comp.code_nature
                        && !natures.some(n => n.code === comp.code_nature) && (
                        <option key="__current" value={comp.code_nature}>
                          {comp.code_nature} — (valeur actuelle)
                        </option>
                      )}
                      {natures.map(n => (
                        <option key={n.code} value={n.code}>
                          {n.code} — {n.description}
                        </option>
                      ))}
                    </select>
                  </Cell>
                  <Cell label="Nature accessoire">
                    <select value={comp.nature_accessoire}
                            onChange={e => change("nature_accessoire", e.target.value)}
                            disabled={disabled} style={{ ...inp, background: "#fff" }}
                            data-testid="comp-nat-acces">
                      <option value="">-- Sélectionner --</option>
                      {/* Valeur DB non listée (nature inactive ou hors filtre)
                          → affichée en tête pour ne pas perdre la donnée. */}
                      {comp.nature_accessoire
                        && !natures.some(n => n.code === comp.nature_accessoire) && (
                        <option key="__current" value={comp.nature_accessoire}>
                          {comp.nature_accessoire} — (valeur actuelle)
                        </option>
                      )}
                      {natures.map(n => (
                        <option key={n.code} value={n.code}>
                          {n.code} — {n.description}
                        </option>
                      ))}
                    </select>
                  </Cell>
                  <Cell label="No dossier de cour">
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <input value={compCourSansMasque ? (comp.no_dossier_cour || '') : maskCourNo(comp.no_dossier_cour || '')}
                             ref={courInputRef}
                             onChange={e => change("no_dossier_cour", compCourSansMasque ? e.target.value : maskCourNo(e.target.value))}
                             onBlur={e => handleCompCourBlur(e.target.value)}
                             disabled={disabled}
                             placeholder="000-00-000000-000" style={inp}
                             data-testid="comp-no-dossier" />
                      <button
                        type="button"
                        onClick={() => {
                          if (!comp.pk_daj) {
                            showPopup?.("Enregistrez d'abord la comparution.",
                                        "warning", "Numéros de cause");
                            return;
                          }
                          setShowInfocour(true);
                        }}
                        title="Ouvrir la fenêtre Numéros de cause (dossiers de cour multiples)"
                        data-testid="btn-open-nocauses"
                        style={{
                          padding: "2px 8px", fontSize: 14, fontWeight: 700,
                          cursor: "pointer", lineHeight: 1,
                          background: "linear-gradient(to bottom, #f0f0f0, #d0d0d0)",
                          border: "1px solid #808080", borderRadius: 2,
                          color: "#333", minWidth: 26,
                        }}>…</button>
                      <label style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, whiteSpace: "nowrap" }}>
                        <input type="checkbox" checked={compCourSansMasque} disabled={disabled}
                               onChange={e => setCompCourSansMasque(e.target.checked)}
                               data-testid="comp-chk-cour-sans-masque" />
                        Sans masque
                      </label>
                    </div>
                  </Cell>
                  <Cell label="Étape">
                    <select value={comp.etape}
                            onChange={e => change("etape", e.target.value)}
                            disabled={disabled} style={{ ...inp, background: "#fff" }}
                            data-testid="comp-etape">
                      <option value="">-- Sélectionner --</option>
                      {comp.etape
                        && !etapes.some(e => e.description === comp.etape) && (
                        <option key="__current" value={comp.etape}>
                          {comp.etape} (valeur actuelle)
                        </option>
                      )}
                      {etapes.map(e => (
                        <option key={e.code} value={e.description}>
                          {e.description}
                        </option>
                      ))}
                    </select>
                  </Cell>
                </tbody></table>
              </div>
            </div>
          )}

          {activeTab === "rencontre" && (
            <div>
              <div style={{ background: "#0d7377", color: "#fff",
                             padding: "4px 8px", fontSize: 11, fontWeight: 700,
                             textAlign: "center", marginBottom: 6 }}>Rencontre</div>

              {/* Grid des rencontres */}
              <table style={{ borderCollapse: "collapse", width: "100%",
                              fontSize: 11, marginBottom: 8 }}>
                <thead>
                  <tr style={{ background: "#c8d4dc" }}>
                    <th style={{ ...cellStyle, background: "#c8d4dc" }}>Date</th>
                    <th style={{ ...cellStyle, background: "#c8d4dc" }}>Heure</th>
                    <th style={{ ...cellStyle, background: "#c8d4dc" }}>Lieu</th>
                    <th style={{ ...cellStyle, background: "#c8d4dc" }}>Local</th>
                    <th style={{ ...cellStyle, background: "#c8d4dc" }}>Rencontré ?</th>
                    <th style={{ ...cellStyle, background: "#c8d4dc" }}>Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="comp-renc-grid">
                  {rencontres.length === 0 && (
                    <tr><td colSpan={6} style={{ ...cellStyle, textAlign: "center",
                                                  color: "#666" }}>Aucune rencontre</td></tr>
                  )}
                  {rencontres.map(r => (
                    <tr key={r.rowid}>
                      <td style={cellStyle}>{(r.date || "").slice(0, 10)}</td>
                      <td style={cellStyle}>{r.heure}</td>
                      <td style={cellStyle}>{r.lieu}</td>
                      <td style={cellStyle}>{r.local}</td>
                      <td style={cellStyle}>{r.rencontre === 1 ? "Oui" : "Non"}</td>
                      <td style={cellStyle}>
                        <button onClick={() => setRencForm(r)}
                                data-testid={`renc-edit-${r.rowid}`}
                                style={{ marginRight: 4, cursor: "pointer" }}>Éditer</button>
                        <button onClick={() => delRenc(r.rowid)}
                                data-testid={`renc-del-${r.rowid}`}
                                style={{ cursor: "pointer", color: "#c00" }}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Formulaire rencontre */}
              <div style={{ background: "#f5f5f5", border: "1px solid #808080", padding: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11 }}>
                  {rencForm.rowid ? "Modifier rencontre" : "Nouvelle rencontre"}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                    <Cell label="Date">
                      <DateInput value={rencForm.date}
                                 onChange={v => setRencForm(f => ({ ...f, date: v }))}
                                 data-testid="renc-date" />
                    </Cell>
                    <Cell label="Heure">
                      <input value={rencForm.heure}
                             onChange={e => setRencForm(f => ({ ...f, heure: e.target.value }))}
                             placeholder="HH:MM" style={inp} data-testid="renc-heure" />
                    </Cell>
                    <Cell label="Lieu">
                      <select value={rencForm.lieu}
                              onChange={e => setRencForm(f => ({ ...f, lieu: e.target.value }))}
                              style={{ ...inp, background: "#fff" }} data-testid="renc-lieu">
                        <option value="">--</option>
                        {lieux.map(l => (
                          <option key={l.code} value={l.description}>
                            {l.description}
                          </option>
                        ))}
                      </select>
                    </Cell>
                    <Cell label="Local">
                      <input value={rencForm.local}
                             onChange={e => setRencForm(f => ({ ...f, local: e.target.value }))}
                             style={inp} data-testid="renc-local" />
                    </Cell>
                  </tbody></table>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                    <Cell label="Rencontré ?">
                      <label style={{ marginRight: 12 }}>
                        <input type="radio" checked={rencForm.rencontre === 1}
                               onChange={() => setRencForm(f => ({ ...f, rencontre: 1 }))}
                               data-testid="renc-oui" /> Oui
                      </label>
                      <label>
                        <input type="radio" checked={rencForm.rencontre === 0}
                               onChange={() => setRencForm(f => ({ ...f, rencontre: 0 }))}
                               data-testid="renc-non" /> Non
                      </label>
                    </Cell>
                    <Cell label="Tél. (1)">
                      <input value={rencForm.tel1}
                             onChange={e => setRencForm(f => ({ ...f, tel1: e.target.value }))}
                             style={inp} data-testid="renc-tel1" />
                    </Cell>
                    <Cell label="Tél. (2)">
                      <input value={rencForm.tel2}
                             onChange={e => setRencForm(f => ({ ...f, tel2: e.target.value }))}
                             style={inp} data-testid="renc-tel2" />
                    </Cell>
                    <Cell label="Remarque">
                      <textarea value={rencForm.remarque}
                                onChange={e => setRencForm(f => ({ ...f, remarque: e.target.value }))}
                                rows={2} style={{ ...inp, resize: "vertical" }}
                                data-testid="renc-remarque" />
                    </Cell>
                  </tbody></table>
                </div>
                <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                  <WinButton IconComponent={Save} onClick={saveRenc}
                             disabled={!comp.pk_daj}
                             data-testid="btn-renc-save">
                    {rencForm.rowid ? "Modifier" : "Ajouter"}
                  </WinButton>
                  {rencForm.rowid && (
                    <WinButton IconComponent={FilePlus}
                               onClick={() => setRencForm(EMPTY_RENC)}
                               data-testid="btn-renc-annuler">Annuler</WinButton>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modale Recherche */}
        {showRecherche && (
          <div style={{ position: "absolute", inset: 0,
                         background: "rgba(0,0,0,0.4)", display: "flex",
                         alignItems: "center", justifyContent: "center" }}
               onClick={() => setShowRecherche(false)}>
            <div onClick={e => e.stopPropagation()} data-testid="comp-recherche-modal"
                 style={{ background: "#dbe6f0", border: "1px solid #808080",
                          width: "min(880px, 96vw)", maxHeight: "88vh", overflow: "auto",
                          padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                             marginBottom: 8 }}>
                <b>Recherche de demandes</b>
                <button onClick={() => setShowRecherche(false)}
                        data-testid="btn-recherche-close"
                        style={{ background: "transparent", border: "none",
                                 cursor: "pointer", fontWeight: 700, fontSize: 14 }}>×</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}><tbody>
                  <Cell label="Nom">
                    <input value={searchCrit.nom}
                           onChange={e => setSearchCrit(c => ({ ...c, nom: e.target.value }))}
                           onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
                           style={inp} data-testid="search-nom" autoFocus />
                  </Cell>
                  <Cell label="Prénom">
                    <input value={searchCrit.prenom}
                           onChange={e => setSearchCrit(c => ({ ...c, prenom: e.target.value }))}
                           onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
                           style={inp} data-testid="search-prenom" />
                  </Cell>
                  <Cell label="Date de naissance">
                    <DateInput value={searchCrit.date_naissance}
                               onChange={v => setSearchCrit(c => ({ ...c, date_naissance: v }))}
                               data-testid="search-dnaiss" />
                  </Cell>
                  <Cell label="No. séquence">
                    <input value={searchCrit.noseqdaj}
                           onChange={e => setSearchCrit(c => ({ ...c, noseqdaj: e.target.value }))}
                           onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
                           style={inp} data-testid="search-noseq" />
                  </Cell>
                </tbody></table>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}><tbody>
                  <Cell label="No. dossier cour">
                    <input value={searchCrit.no_dossier_cour}
                           onChange={e => setSearchCrit(c => ({ ...c, no_dossier_cour: e.target.value }))}
                           onKeyDown={e => { if (e.key === "Enter") doSearch(); }}
                           style={inp} data-testid="search-dossier-cour" />
                  </Cell>
                  <Cell label="Région">
                    <input value={searchCrit.noreg}
                           onChange={e => setSearchCrit(c => ({ ...c, noreg: e.target.value }))}
                           style={{ ...inp, width: 60 }} data-testid="search-noreg" />
                  </Cell>
                  <Cell label="Bureau">
                    <input value={searchCrit.nobur}
                           onChange={e => setSearchCrit(c => ({ ...c, nobur: e.target.value }))}
                           style={{ ...inp, width: 60 }} data-testid="search-nobur" />
                  </Cell>
                </tbody></table>
              </div>

              {/* Barre d'actions — boutons visibles et centrés */}
              <div style={{
                marginTop: 12, padding: "10px 12px",
                background: "#e8eef4", border: "1px solid #b0c0cf",
                display: "flex", gap: 10, justifyContent: "center",
                alignItems: "center",
              }}>
                <button
                  type="button"
                  onClick={doSearch}
                  data-testid="btn-search-go"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "8px 22px", fontSize: 12, fontWeight: 700,
                    cursor: "pointer", color: "#fff",
                    background: "linear-gradient(to bottom, #2b6cb0, #1e4d80)",
                    border: "1px solid #133b6b",
                    borderRadius: 3, minWidth: 140,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                  }}
                  onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                  onMouseLeave={e => e.currentTarget.style.filter = "none"}
                >
                  <Search size={16} strokeWidth={2.5} />
                  Rechercher
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSearchCrit({
                      nom: "", prenom: "", date_naissance: "",
                      noseqdaj: "", no_dossier_cour: "",
                      noreg: searchCrit.noreg, nobur: searchCrit.nobur,
                    });
                    setSearchResults([]);
                  }}
                  data-testid="btn-search-clear"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "8px 20px", fontSize: 12, fontWeight: 600,
                    cursor: "pointer", color: "#333",
                    background: "linear-gradient(to bottom, #f0f0f0, #d8d8d8)",
                    border: "1px solid #808080",
                    borderRadius: 3, minWidth: 100,
                  }}
                >
                  Effacer
                </button>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "#555" }}>
                  <b>Astuce :</b> pressez <kbd style={{
                    padding: "1px 4px", border: "1px solid #999",
                    borderRadius: 2, background: "#fff",
                  }}>Entrée</kbd> pour lancer la recherche
                </span>
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, marginBottom: 4 }}>
                  Nombre de lignes : <b>{searchResults.length}</b></div>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
                  <thead><tr style={{ background: "#c8d4dc" }}>
                    <th style={cellStyle}>Nom Requérant</th>
                    <th style={cellStyle}>Date Nais.</th>
                    <th style={cellStyle}>No. bur</th>
                    <th style={cellStyle}>No séq.</th>
                    <th style={cellStyle}>Date dem.</th>
                    <th style={cellStyle}>No cause</th>
                    <th style={cellStyle}>Nature</th>
                    <th style={cellStyle}></th>
                  </tr></thead>
                  <tbody data-testid="search-results">
                    {searchResults.length === 0 && (
                      <tr><td colSpan={8} style={{ ...cellStyle, textAlign: "center",
                                                    color: "#666" }}>Aucun résultat</td></tr>
                    )}
                    {searchResults.map(r => {
                      const rowKey = r.pk_daj || `${r.noreg}${r.nobur}${r.noseqdaj}`;
                      return (
                        <tr key={rowKey}
                            data-testid={`search-row-${rowKey}`}
                            onDoubleClick={() => doOpen(r.pk_daj, r)}
                            onMouseEnter={e => e.currentTarget.style.background = "#e3ecf5"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                            style={{ cursor: "pointer" }}
                            title="Double-cliquez pour ouvrir cette comparution">
                          <td style={cellStyle}>{r.nom} {r.prenom}</td>
                          <td style={cellStyle}>{(r.date_naissance || "").slice(0, 10)}</td>
                          <td style={cellStyle}>{r.nobur}</td>
                          <td style={cellStyle}>{r.noseqdaj}</td>
                          <td style={cellStyle}>{(r.date_appel || "").slice(0, 10)}</td>
                          <td style={cellStyle}>{r.no_cause}</td>
                          <td style={cellStyle}>{r.code_nature}</td>
                          <td style={cellStyle}>
                            <button
                              type="button"
                              onClick={ev => { ev.stopPropagation(); doOpen(r.pk_daj, r); }}
                              data-testid={`search-open-${rowKey}`}
                              style={{
                                padding: "3px 12px", cursor: "pointer",
                                background: "linear-gradient(to bottom, #2b6cb0, #1e4d80)",
                                color: "#fff", border: "1px solid #133b6b",
                                borderRadius: 2, fontSize: 11, fontWeight: 600,
                              }}>Ouvrir</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Modale Numéros de cause — parité `FrmNoCauses.vb` legacy */}
        {showInfocour && (
          <div style={{ position: "absolute", inset: 0,
                         background: "rgba(0,0,0,0.4)", display: "flex",
                         alignItems: "center", justifyContent: "center" }}
               onClick={icCloseModal}>
            <div onClick={e => e.stopPropagation()} data-testid="comp-infocour-modal"
                 style={{ background: "#dbe6f0", border: "1px solid #808080",
                          width: "min(1080px, 98vw)", maxHeight: "88vh",
                          overflow: "auto", display: "flex",
                          flexDirection: "column" }}>
              {/* Titre */}
              <div style={{
                background: "linear-gradient(to bottom, #dbe6f0, #b8ccd8)",
                padding: "4px 8px", fontSize: 12, fontWeight: 600,
                color: "#111", borderBottom: "1px solid #808080",
                display: "flex", justifyContent: "space-between",
              }}>
                <span>Numéros de cause</span>
                <button onClick={icCloseModal} data-testid="btn-infocour-close"
                        style={{ background: "transparent", border: "none",
                                 cursor: "pointer", fontWeight: 700, fontSize: 14 }}>×</button>
              </div>

              {/* Toolbar — boutons Windows-style bien contrastés */}
              <div style={{ padding: "8px 12px", gap: 6, display: "flex",
                              background: "#dbe6f0", flexWrap: "wrap",
                              borderBottom: "1px solid #808080" }}>
                {(() => {
                  const btnStyle = (enabled) => ({
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "6px 14px", fontSize: 11, fontWeight: 600,
                    cursor: enabled ? "pointer" : "not-allowed",
                    color: enabled ? "#fff" : "#666",
                    background: enabled
                      ? "linear-gradient(to bottom, #4a6584, #2b3f5c)"
                      : "#c4c4c4",
                    border: enabled ? "1px solid #1a2942" : "1px solid #888",
                    borderRadius: 3, minWidth: 100, justifyContent: "center",
                    opacity: enabled ? 1 : 0.7,
                  });
                  const items = [
                    { id: "nouveau", label: "Nouveau", Icon: FilePlus,
                      enabled: true, on: icNouveau },
                    { id: "enregistrer", label: "Enregistrer", Icon: Save,
                      enabled: icMode !== "view", on: icEnregistrer },
                    { id: "modifier", label: "Modifier", Icon: Edit,
                      enabled: !!icSelectedRowId && icMode === "view",
                      on: icModifier },
                    { id: "supprimer", label: "Supprimer", Icon: Trash2,
                      enabled: !!icSelectedRowId, on: icSupprimer },
                    { id: "fermer", label: "Fermer", Icon: DoorClosed,
                      enabled: true, on: icCloseModal },
                  ];
                  return items.map(b => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={b.enabled ? b.on : undefined}
                      disabled={!b.enabled}
                      data-testid={`btn-ic-${b.id}`}
                      style={btnStyle(b.enabled)}
                    >
                      <b.Icon size={14} strokeWidth={2.2} />
                      {b.label}
                    </button>
                  ));
                })()}
              </div>

              {/* Bandeau "Informations" */}
              <div style={{ background: "#0d7377", color: "#fff",
                              padding: "4px 8px", fontSize: 11, fontWeight: 700,
                              textAlign: "center" }}>Informations</div>

              {/* Corps : liste à gauche + formulaire à droite */}
              <div style={{ display: "grid",
                              gridTemplateColumns: "340px 1fr",
                              gap: 8, padding: 8 }}>
                {/* Grid gauche : No cause + Code nature */}
                <table style={{ borderCollapse: "collapse", width: "100%",
                                  fontSize: 11, alignSelf: "start" }}>
                  <thead>
                    <tr style={{ background: "#c8d4dc" }}>
                      <th style={{ ...cellStyle, width: 24 }}></th>
                      <th style={cellStyle}>No. cause</th>
                      <th style={cellStyle}>Code nature</th>
                    </tr>
                  </thead>
                  <tbody data-testid="infocour-list">
                    {infocours.length === 0 && (
                      <tr><td colSpan={3}
                              style={{ ...cellStyle, textAlign: "center",
                                       color: "#666", padding: 12 }}>
                        Aucune cause enregistrée
                      </td></tr>
                    )}
                    {infocours.map(ic => {
                      const sel = ic.rowid === icSelectedRowId;
                      return (
                        <tr key={ic.rowid}
                            data-testid={`ic-row-${ic.rowid}`}
                            onClick={() => selectIcRow(ic)}
                            style={{ cursor: "pointer",
                                     background: sel ? "#316ac5" : "#fff",
                                     color: sel ? "#fff" : "#000" }}>
                          <td style={{ ...cellStyle, textAlign: "center",
                                       color: sel ? "#fff" : "#333" }}>
                            {sel ? "▶" : ""}
                          </td>
                          <td style={{ ...cellStyle, background: "inherit",
                                       color: "inherit" }}>{ic.no_cause}</td>
                          <td style={{ ...cellStyle, background: "inherit",
                                       color: "inherit" }}>{ic.code_nature}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Formulaire droit */}
                <div style={{ background: "#fff", border: "1px solid #808080",
                                padding: 8 }}>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <tbody>
                      <Cell label="Article">
                        <input value={icForm.article}
                               onChange={e => setIcForm(f => ({ ...f, article: e.target.value }))}
                               disabled={icMode === "view"}
                               style={{ ...inp, background: icMode === "view" ? "#e8e8e8" : "#fff" }}
                               data-testid="ic-article" />
                      </Cell>
                      <Cell label="Code Nature">
                        <select value={icForm.code_nature}
                                onChange={e => setIcForm(f => ({ ...f, code_nature: e.target.value }))}
                                disabled={icMode === "view"}
                                style={{ ...inp, background: "#fff" }}
                                data-testid="ic-code-nature">
                          <option value="">-- Sélectionner --</option>
                          {icForm.code_nature
                            && !natures.some(n => n.code === icForm.code_nature) && (
                            <option key="__cur" value={icForm.code_nature}>
                              {icForm.code_nature} — (valeur actuelle)
                            </option>
                          )}
                          {natures.map(n => (
                            <option key={n.code} value={n.code}>
                              {n.code} — {n.description}
                            </option>
                          ))}
                        </select>
                      </Cell>
                      <Cell label="Nature accessoire">
                        <select value={icForm.code_criminel}
                                onChange={e => setIcForm(f => ({ ...f, code_criminel: e.target.value }))}
                                disabled={icMode === "view"}
                                style={{ ...inp, background: "#fff" }}
                                data-testid="ic-nature-acces">
                          <option value="">-- Sélectionner --</option>
                          {icForm.code_criminel
                            && !natures.some(n => n.code === icForm.code_criminel) && (
                            <option key="__cur" value={icForm.code_criminel}>
                              {icForm.code_criminel} — (valeur actuelle)
                            </option>
                          )}
                          {natures.map(n => (
                            <option key={n.code} value={n.code}>
                              {n.code} — {n.description}
                            </option>
                          ))}
                        </select>
                      </Cell>
                    </tbody>
                  </table>

                  <div style={{ height: 12 }} />

                  <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <tbody>
                      <Cell label="No. de cause">
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input value={icForm.sans_mask ? (icForm.no_cause || '') : maskCourNo(icForm.no_cause || '')}
                                 ref={icCourInputRef}
                                 onChange={e => setIcForm(f => ({ ...f, no_cause: f.sans_mask ? e.target.value : maskCourNo(e.target.value) }))}
                                 onBlur={e => handleIcCourBlur(e.target.value)}
                                 disabled={icMode === "view"}
                                 placeholder="000-00-000000-000"
                                 style={{ ...inp,
                                          background: icMode === "view" ? "#e8e8e8" : "#fff",
                                          fontFamily: "monospace" }}
                                 data-testid="ic-no-cause" />
                          <label style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                            <input type="checkbox" checked={icForm.sans_mask}
                                   onChange={e => setIcForm(f => ({ ...f, sans_mask: e.target.checked }))}
                                   disabled={icMode === "view"}
                                   data-testid="ic-sans-mask" />
                            {" "}Sans Mask
                          </label>
                        </div>
                      </Cell>
                      <Cell label="Étape">
                        <select value={icForm.etape}
                                onChange={e => setIcForm(f => ({ ...f, etape: e.target.value }))}
                                disabled={icMode === "view"}
                                style={{ ...inp, background: "#fff" }}
                                data-testid="ic-etape">
                          <option value="">-- Sélectionner --</option>
                          {icForm.etape
                            && !etapes.some(x => x.description === icForm.etape) && (
                            <option key="__cur" value={icForm.etape}>
                              {icForm.etape} (valeur actuelle)
                            </option>
                          )}
                          {etapes.map(x => (
                            <option key={x.code} value={x.description}>
                              {x.description}
                            </option>
                          ))}
                        </select>
                      </Cell>
                    </tbody>
                  </table>

                  <div style={{ height: 8 }} />
                  <textarea value={icForm.note}
                            onChange={e => setIcForm(f => ({ ...f, note: e.target.value }))}
                            disabled={icMode === "view"}
                            rows={4}
                            placeholder="Notes / remarques…"
                            style={{ width: "100%", fontSize: 11,
                                     border: "1px solid #808080",
                                     padding: 4,
                                     background: icMode === "view" ? "#e8e8e8" : "#fff",
                                     resize: "vertical", fontFamily: "inherit" }}
                            data-testid="ic-note" />
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
